import { IconButton } from '@vrplatform/components';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { formatCurrency } from '@vrplatform/utils';
import { Group, Text, rem } from '@mantine/core';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { useReservationFinancialsModals } from '../_hooks';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';
import { ReservationFinancialAccountTooltip } from './ReservationFinancialAccountTooltip';
import { ReservationPostingDatePreviewTooltip } from './ReservationPostingDatePreviewTooltip';
import { formatFinancialLockMessage } from '../_utils';
import { HiddenFeatureIndicator } from '../../../components';

type FinancialsRow = Reservation['financials'][0];

export const getIsReservationFinancialUnmapped = (
  line: Reservation['financials'][number]
) => {
  return (
    !line.account?.id &&
    line.type === 'financial' &&
    !!line.reservationLine?.type
  );
};

type Props = {
  reservation: {
    id: string;
    totals: Reservation['totals'];
    currency: Reservation['currency'];
    isManualReservation: Reservation['isManualReservation'];
  };
  openDeleteAdjustment: ReturnType<
    typeof useReservationFinancialsModals
  >['remove']['setOpened'];
  openEditAdjustment: ReturnType<
    typeof useReservationFinancialsModals
  >['edit']['setOpened'];
  openReclassifyLine: ReturnType<
    typeof useReservationFinancialsModals
  >['reclassify']['setOpened'];
};

export const useFinancialsSectionColumns = ({
  openDeleteAdjustment,
  openEditAdjustment,
  openReclassifyLine,
  reservation: {
    id: reservationId,
    totals: { guest },
    currency,
    isManualReservation,
  },
}: Props) =>
  useMemo<MRT_ColumnDef<FinancialsRow>[]>(
    () => [
      {
        header: 'Description',
        mantineTableFooterCellProps: {
          pl: 'xs',
        },
        mantineTableBodyCellProps: {
          px: 'xs',
        },
        Footer: () => (
          <Text span fw={500}>
            Total guest charged
          </Text>
        ),
        Cell: ({ row }) => {
          const goto = useNavigate();

          const description = row.original.description;
          const td =
            row.original.status === 'inactive' ? 'line-through' : undefined;

          const isUnmapped = getIsReservationFinancialUnmapped(row.original);

          if (row.original.type === 'adjustment')
            return (
              <ReservationFinancialAccountTooltip
                account={row.original.account}
              >
                <Text ta="left" td={td}>
                  {description}
                </Text>
                <Text size="xs" ta="left" c="gray">
                  Financials Adjustment
                </Text>
              </ReservationFinancialAccountTooltip>
            );

          return (
            <ReservationFinancialAccountTooltip account={row.original.account}>
              <Group gap={rem(8)} wrap="nowrap">
                {isUnmapped && (
                  <IconButton
                    onClick={() =>
                      goto(
                        '/settings/accounting/reservations?accountId=unmapped'
                      )
                    }
                    disabled={!row.original.reservationLine?.type}
                    icon="AlertTriangleIcon"
                    iconSize={16}
                    tooltip="Financial line is not linked to an account"
                    color={(theme) => theme.colors.red[6]}
                  />
                )}
                <Text ta="left" td={td}>
                  {description}
                </Text>
              </Group>
            </ReservationFinancialAccountTooltip>
          );
        },
      },
      {
        header: 'amount',
        maxSize: 100,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
          pr: 'xs',
        },
        Cell: ({ row }) => {
          const amount = formatCurrency(
            (row.original.centTotal || 0) / 100,
            currency
          );

          const locked = row.original.lock?.status === 'locked';

          const td =
            row.original.status === 'inactive' ? 'line-through' : undefined;

          const isUnmapped = getIsReservationFinancialUnmapped(row.original);

          const showDeleteButton = isManualReservation
            ? row.original.type === 'adjustment' || locked === false
            : row.original.type === 'adjustment';

          return (
            <Group gap={5} wrap="nowrap">
              {!isUnmapped &&
                (row.original.type === 'adjustment' ? (
                  <EditAdjustmentButtons
                    row={row.original}
                    reservationId={reservationId}
                    openEditAdjustment={openEditAdjustment}
                    openDeleteAdjustment={openDeleteAdjustment}
                  />
                ) : showDeleteButton ? (
                  <DeleteButton
                    isManualReservation={isManualReservation}
                    row={row.original}
                    reservationId={reservationId}
                    openDeleteAdjustment={openDeleteAdjustment}
                  />
                ) : (
                  <AddAdjustmentButtons
                    openEditAdjustment={openEditAdjustment}
                    openReclassifyLine={openReclassifyLine}
                    isManualReservation={isManualReservation}
                    reservationId={reservationId}
                    row={row.original}
                  />
                ))}
              <ReservationPostingDatePreviewTooltip
                postingDatePreview={row.original.postingDatePreview}
                currency={currency}
              >
                <Text td={td}>{amount}</Text>
              </ReservationPostingDatePreviewTooltip>
            </Group>
          );
        },
        Footer: () => (
          <Text span fw={500} ta="right" display={'block'}>
            {formatCurrency((guest ?? 0) / 100, currency)}
          </Text>
        ),
      },
    ],
    [
      currency,
      guest,
      reservationId,
      openDeleteAdjustment,
      openEditAdjustment,
      openReclassifyLine,
      isManualReservation,
    ]
  );

const DeleteButton = ({
  isManualReservation,
  row,
  reservationId,
  openDeleteAdjustment,
}: {
  isManualReservation: boolean;
  row: FinancialsRow;
  reservationId: string;
  openDeleteAdjustment: Props['openDeleteAdjustment'];
}) => {
  const locked = row.lock?.status === 'locked';

  const lockReason = useMemo<string | undefined>(() => {
    return formatFinancialLockMessage(row.lock);
  }, [row.lock]);

  const onClick = useCallback(() => {
    if (!row.reservationLine?.id) return;

    openDeleteAdjustment({
      reservationId,
      id: row.reservationLine.id,
      type: row.type === 'adjustment' ? 'adjustment' : 'financial',
    });
  }, [row.reservationLine?.id, reservationId, openDeleteAdjustment, row.type]);

  if (isManualReservation === false && row.type === 'financial') return null;

  return (
    <IconButton
      icon="TrashIcon"
      iconSize={16}
      disabled={!row.reservationLine?.id || locked}
      tooltip={
        lockReason ||
        (row.type === 'financial' ? 'Delete financial' : 'Delete adjustment')
      }
      className="row-action-icon delete-adjustment-icon"
      onClick={onClick}
    />
  );
};

const EditAdjustmentButtons = ({
  row,
  reservationId,
  openDeleteAdjustment,
  openEditAdjustment,
}: {
  row: FinancialsRow;
  reservationId: string;
  openDeleteAdjustment: Props['openDeleteAdjustment'];
  openEditAdjustment: Props['openEditAdjustment'];
}) => {
  const locked = row.lock?.status === 'locked';

  const lockReason = useMemo<string | undefined>(() => {
    return formatFinancialLockMessage(row.lock);
  }, [row.lock]);

  const onEdit = useCallback(() => {
    if (!row.reservationLine?.id || !row.reservationLine?.type) return;

    openEditAdjustment({
      id: row.reservationLine.id,
      centTotal: row.centTotal,
      description: row.description,
      lineType: row.reservationLine.type,
      party: 'owners',
      postingAt: row.postingAt ?? null,
      reservationId,
      type: 'adjustment',
    });
  }, [openEditAdjustment, reservationId, row]);

  return (
    <>
      <IconButton
        icon="Edit3Icon"
        iconSize={16}
        disabled={
          !row.reservationLine?.id || !row.reservationLine?.type || locked
        }
        tooltip={lockReason || 'Edit adjustment'}
        className="row-action-icon edit-adjustment-icon"
        onClick={onEdit}
      />
      <DeleteButton
        isManualReservation={false}
        row={row}
        reservationId={reservationId}
        openDeleteAdjustment={openDeleteAdjustment}
      />
    </>
  );
};

const AddAdjustmentButtons = ({
  openEditAdjustment,
  openReclassifyLine,
  reservationId,
  row,
  isManualReservation,
}: {
  row: FinancialsRow;
  openEditAdjustment: Props['openEditAdjustment'];
  openReclassifyLine: Props['openReclassifyLine'];
  reservationId: string;
  isManualReservation: boolean;
}) => {
  const lineType = row.reservationLine?.type;
  const lineId = row.reservationLine?.id;

  if (!lineType || !lineId) return null;

  return (
    <>
      <IconButton
        icon="PercentageRecIcon"
        iconSize={16}
        tooltip="Add manager fee for this item"
        onClick={() =>
          openEditAdjustment({
            centTotal: undefined,
            description: `${row.description} fee`,
            lineType,
            party: 'manager',
            postingAt: null,
            reservationId,
            type: 'adjustment',
          })
        }
        className="row-action-icon add-adjustment-icon"
      />
      <IconButton
        icon="PlusRecIcon"
        iconSize={16}
        tooltip="Add financial adjustment"
        className="row-action-icon add-adjustment-icon"
        onClick={() =>
          openEditAdjustment({
            centTotal: undefined,
            description: `${row.description} adjustment`,
            lineType,
            party: 'owners',
            postingAt: null,
            reservationId,
            type: 'adjustment',
          })
        }
      />
      <HiddenFeatureIndicator
        permission="vrp-admin"
        className="row-action-icon add-adjustment-icon"
        hideTooltip
      >
        {!isManualReservation && (
          <IconButton
            icon="ForwardRecIcon"
            iconSize={16}
            tooltip="Reclassify line"
            onClick={() =>
              openReclassifyLine({
                reservationId,
                sourceLineId: lineId,
                sourceLineType: lineType,
                sourceDescription: row.description,
                sourceCentTotal: row.centTotal,
              })
            }
            className="row-action-icon add-adjustment-icon"
          />
        )}
      </HiddenFeatureIndicator>
    </>
  );
};
